import {api} from "@/shared/api";
import {GroupDropdown} from "@/shared/ui/groupDropdown";
import {Combobox} from "@/shared/ui/combobox";
import {BaseTree, BaseTreeNodeData} from "@/shared/ui/baseTree";
import {FancytreeContextMenu} from "@/shared/ui/fancytreeContextMenu";
import * as EntityLinks from "@/shared/entityLinks";
import {TaskAdministrationDialog} from "@/features/task/taskAdministrationDialog";
import {FormUtil} from "@/shared/forms/formUtil";
import {breadcrumbsWithoutName, htmlEscape} from "@/shared/utils/string";
import {BrowserStorageSettings} from "@/shared/browserStorageSettings";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import * as Input from "@/shared/utils/input";
import {debounce} from "@/shared/utils/misc";
import {Notify} from "@/shared/ui/notify";
import {SecurityStatus} from "@/shared/securityStatus";
import {ErrorResponse, Identifiable, Task, User} from "@/shared/timr";
import {DownloadStatus} from "@/shared/ui/downloadStatus";
import {ExportType} from "@/shared/timrTypes";
import FancytreeNode = Fancytree.FancytreeNode;
import FancytreeDndData = Fancytree.FancytreeDndData;

import Modal = BootstrapModal.Modal;

export interface TaskAdministrationConfig {
	isTaskLeader: boolean;
	showInactiveTasks: boolean;
	billingAddressCountryId?: number;
	users: User[];
	taskLeaders: User[];
}

export interface ClipboardTask extends Identifiable {
	name: string;
}

export class TaskAdministrationPage {
	private static GROUP_TASK_ADMINISTRATION = "taskAdministration";

	private tree: BaseTree;
	private taskDialog: TaskAdministrationDialog = null;
	private clipboardTask: ClipboardTask = {id: null, name: ""};
	private taskPasteDialog: Modal;
	private taskOnTaskDialog: Modal = null;
	private taskPasteId: number;
	private dragDropData: FancytreeDndData;

	private searchFilter: JQuery;
	private groupFilter: GroupDropdown;
	private userFilter: Combobox<User>;
	private taskLeaderFilter: Combobox<User>;

	constructor(public config: TaskAdministrationConfig) {
		this.initFilter();
		this.initTree();
		this.initEventListeners();
		this.initTaskDialog();
	}

	private initFilter() {
		this.searchFilter = $("#searchFilter");
		this.searchFilter.on("input", debounce(e => {
			this.filterTree();
		}, this, 350));

		const showInactiveCheckboxSelector = $("#showInactiveCheckbox");
		const showInactiveActive = BrowserStorageSettings.getTasksShowInactive(TaskAdministrationPage.GROUP_TASK_ADMINISTRATION);
		showInactiveCheckboxSelector.prop("checked", showInactiveActive !== null ? showInactiveActive : this.config.showInactiveTasks);
		showInactiveCheckboxSelector.click(() => {
			BrowserStorageSettings.setTasksShowInactive(TaskAdministrationPage.GROUP_TASK_ADMINISTRATION, showInactiveCheckboxSelector.is(":checked"));
			this.tree.reload();
		});

		if (SecurityStatus.isCorporateFeaturesAllowed()) {
			this.groupFilter = new GroupDropdown({
				rootElement: $("#taskFilterGroupDropdown"),
				allowSelectionOfNotAssigned: false,
				emptyMessage: messages["group.filter.all"],
				clearEntryMessage: messages["group.filter.all"],
			}, {
				loadRootNodesURL: "reports/loadRootGroups.json",
				loadSubNodesURL: "reports/loadSubGroups.json",
				filterNodesURL: "reports/filterGroups.json"
			});
			$(this.groupFilter).on(GroupDropdown.EVENT_SELECTED, () => {
				this.filterTree();
			});

			this.taskLeaderFilter = new Combobox({
				items: this.config.taskLeaders,
				emptyMessage: messages["user.filter.all"],
				formatter: user => {
					return user.fullname;
				},
				idElement: $("#taskFilterTaskLeaderId"),
				filterElement: $("#taskFilterTaskLeaderFilter"),
				caretElement: $("#taskFilterTaskLeaderCaret"),
			});
			$(this.taskLeaderFilter).on(Combobox.EVENT_CHANGED, () => {
				this.filterTree();
			});
		}

		this.userFilter = new Combobox({
			items: this.config.users,
			emptyMessage: messages["user.filter.all"],
			formatter: user => {
				return user.fullname;
			},
			idElement: $("#taskFilterUserId"),
			filterElement: $("#taskFilterUserFilter"),
			caretElement: $("#taskFilterUserCaret"),
		});
		$(this.userFilter).on(Combobox.EVENT_CHANGED, () => {
			this.filterTree();
		});

		$("#searchOrganizationsCheckbox").on("change", () => {
			this.filterTree();
		});
	}

	private filterTree() {
		const searchValue = this.searchFilter.val() as string;
		if (searchValue.length > 0) {
			this.tree.queryTree(searchValue);
		} else if (
			this.groupFilter?.getSelectedId() ||
			this.userFilter?.getSelectedItem() ||
			this.taskLeaderFilter?.getSelectedItem()
		) {
			this.tree.queryTree(null, true);
		} else {
			this.tree.queryTree(null, false);
		}
	}

	private getFilterRequestData() {
		return {
			hideInactive: !$("#showInactiveCheckbox").is(":checked"),
			searchViaGroup: $("#searchOrganizationsCheckbox").is(":checked"),
			userId: this.userFilter?.getSelectedItem()?.id,
			groupId: this.groupFilter?.getSelectedId(),
			taskLeaderId: this.taskLeaderFilter?.getSelectedItem()?.id
		};
	}

	private downloadTaskExport(exportType: ExportType) {
		const searchValue = (this.searchFilter.val() as string).trim();

		void api("tasks/export.json")
			.post({
				...this.getFilterRequestData(),
				// the tree ignores search values below its minimum query length, so does the export
				query: searchValue.length >= 3 ? searchValue : undefined,
				exportFileType: exportType
			})
			.done(data => {
				if (data.exportJobId) {
					new DownloadStatus({
						exportJobId: data.exportJobId,
						exportStatusBaseUrl: "export/status.json",
						exportType: exportType,
						loadingMessage: messages["downloadStatus.loadingMessage.reports"],
						allowCancellation: true
					});
				}
			});
	}

	private initEventListeners() {
		$("#taskExportDropDown a").on("click", e => this.downloadTaskExport($(e.currentTarget).data("exportType") as ExportType));

		$(document).on(TaskAdministrationDialog.EVENT_TASK_SAVED, (event, task: Task) => {
			const node = this.tree.getNodeByKey(task.id + "");

			if (node) {
				node.title = task.name;
				const nodeData = node.data as Task;
				nodeData.name = task.name;
				nodeData.bookable = task.bookable;
				nodeData.disabled = task.disabled;
				node.render(true);
			} else {
				if (task.parentTask) {
					this.reloadAndExpandNode(task.parentTask.id);
				} else {
					this.addNodeToRoot(task);
				}
			}

			if (task.id === this.clipboardTask?.id) {
				this.clipboardTask.name = task.name;
				this.updateCopyDisplay();
			}
		});
		$("#taskAddButton").click(e => {
			e.preventDefault();
			this.showAddTaskDialog();
		});
		$("#pasteAsRootButton").click(() => {
			this.pasteTask(null);
		});
		$("#clearTaskClipboardButton").click(() => {
			this.clearClipboard();
		});
		$(document).on(TaskAdministrationDialog.EVENT_TASK_DELETED, (event, task: { id: number, name: string }) => {
			this.deleteTask(task, true);
		});
	}

	private addNodeToRoot(task: Task) {
		if (!task.disabled || $("#showInactiveCheckbox").is(":checked")) {
			this.tree.getRoot().addNode({
				title: task.name,
				key: task.id.toString(),
				folder: false,
				lazy: false,
				data: {...task}
			}, "firstChild");
		}
	}

	private reloadAndExpandNode(id: number | string) {
		if (id != null) {
			const parent = this.tree.getNodeByKey(id + "");

			if (parent) {
				parent.lazy = true;
				parent.load(true).done(() => {
					parent.setExpanded(true);
				});
			}
		} else {
			this.tree.reload();
		}
	}

	private initTree() {
		this.tree = new BaseTree({
			treeElement: $("#taskTree"),
			loadRootNodesURL: "tasks/loadAllTasks.json",
			loadSubNodesURL: "tasks/taskTreeLoad.json",
			filterNodesURL: "tasks/filterTasks.json",
			getRequestData: () => this.getFilterRequestData(),
			isNodeSelectable: () => false,
			// BaseTree types contextMenu against unknown; the invariant Task menu is safe here since node data is always a Task.
			contextMenu: this.createContextMenu() as FancytreeContextMenu<unknown>,
			onEnhanceTitle: (e, data) => {
				const node = data.node;
				const task = node.data as Task | undefined;
				if (task) {
					node.toggleClass("tree-node-disabled", Boolean(task.disabled));
					node.toggleClass("tree-node-not-recordable", !task.bookable);
				}
			},
			dndSettings: this.getDndSettings(),
			minQueryLength: 3,
			pagingSize: 100,
			clickFolderMode: 3
		});
	}

	private createContextMenu() {
		return new FancytreeContextMenu<Task>(true)
			.addMenuEntry({
				label: "task.context.editTask",
				action: task => this.editTask(task)
			})
			.addDivider()
			.addMenuEntry({
				label: "task.context.addSubtask",
				action: task => {
					if (this.checkAddTaskAllowed()) {
						this.addSubtask(task);
					}
				}
			})
			.addMenuEntry({
				label: "task.context.pasteTask",
				action: task => this.pasteTask(task),
				active: task => this.clipboardTask?.id != null
			})
			.addMenuEntry({
				label: "task.context.copyTask",
				action: task => {
					if (this.checkAddTaskAllowed()) {
						this.copyTask(task);
					}
				}
			})
			.addDivider()
			.addMenuEntry({
				label: "taskLink.copy",
				action: (task: Task) => this.copyTaskLink(task)
			})
			.addDivider()
			.addMenuEntry({
				label: "task.context.disableTask",
				action: task => this.disableTask(task),
				show: task => !task.disabled
			})
			.addMenuEntry({
				label: "task.context.deleteTask",
				action: task => this.deleteTask(task)
			});
	}

	protected getDndSettings() {
		return {
			autoExpandMS: 400,
			focusOnClick: false,
			preventVoidMoves: true,
			preventRecursiveMoves: true,
			dragStart: (node: FancytreeNode, data: FancytreeDndData) => {
				this.tree.config.treeElement.find("ul").addClass("fancytree-hide-menu");
				const nodeData = node.data as BaseTreeNodeData;
				return nodeData?.dummy !== true && node.statusNodeType == null;
			},
			dragEnter: (node: FancytreeNode, data: FancytreeDndData) => {
				const draggedNode = data.otherNode;
				const draggedNodeParent = draggedNode.getParent();
				const nodeParent = node.getParent();

				const dragModes: Fancytree.Extensions.HitMode[] = [];

				// Don't allow drag to its own parent
				if (!draggedNode.isChildOf(node)) {
					dragModes.push("over");
				}

				// Only allow drag "before" to root level and if node is not already at root level
				if (nodeParent && nodeParent.isRootNode() && !(draggedNodeParent && draggedNodeParent.isRootNode())) {
					dragModes.push("before");
				}

				return dragModes;
			},
			dragDrop: (node: FancytreeNode, data: FancytreeDndData) => {
				if (data.hitMode === "over") {
					if (node.isLazy()) {
						node.load();
					}

					this.dragDropData = data;
					this.showDropTaskDialog();
				} else if (data.hitMode === "before") {
					this.dragDropData = {
						otherNode: data.otherNode
					};
					this.dropTaskSave();
				}
			},
			dragStop: (node: FancytreeNode, data: FancytreeDndData) => {
				this.tree.config.treeElement.find("ul").removeClass("fancytree-hide-menu");
			}
		};
	}

	protected showDropTaskDialog() {
		if (this.taskOnTaskDialog === null) {
			this.taskOnTaskDialog = new BootstrapModal.Modal({
				title: "taskOnTask.dialog.title",
				titleSize: "h5",
				message: $("#taskOnTaskDialog"),
				buttons: [{
					text: "taskOnTask.dialog.button.move",
					action: (modal, button) => {
						this.dropTaskSave()
							.done(() => this.taskOnTaskDialog.close())
							.fail(() => Button.resetLoading(button));
					},
					css: "btn-primary"
				}, {
					text: "dialog.cancel",
					action: () => {
						this.taskOnTaskDialog.close();
					}
				}],
				onBeforeOpen: () => {
					this.initTaskOnTaskDialogForCurrentDrop();
				},
				backdrop: true
			});

			$("#taskOnTaskDialog input[name='taskAction']").on("change", () => {
				const selectedAction = this.getSelectedTaskAction();
				if (selectedAction === "merge") {
					$("#taskOnTaskMergeConfirmationCheckbox").prop("checked", false);
				}
				this.updateTaskOnTaskDialogState();
			});
			$("#taskOnTaskMergeConfirmationCheckbox").on("change", () => {
				this.updateTaskOnTaskDialogState();
			});
			this.initTaskOnTaskDialogForCurrentDrop();
		}
		this.taskOnTaskDialog.show();
	}

	private initTaskOnTaskDialogForCurrentDrop() {
		const draggedNode = this.dragDropData?.otherNode;
		const targetNode = this.dragDropData?.node;

		$("#taskOnTaskDialogSourceTask").text(draggedNode?.title || "");
		$("#taskOnTaskDialogTargetTask").text(targetNode?.title || "");
		$("#taskOnTaskDialogSourceTaskPath").html(this.getNodeBreadcrumbs(draggedNode));
		$("#taskOnTaskDialogTargetTaskPath").html(this.getNodeBreadcrumbs(targetNode));

		$("#subtaskActionRadio").prop("checked", true);
		$("#taskOnTaskMergeConfirmationCheckbox").prop("checked", false);
		$("#taskOnTaskDialogMergeHint").toggle(draggedNode?.isFolder() || draggedNode?.hasChildren());

		this.updateTaskOnTaskDialogState();
	}
	private getNodeBreadcrumbs(node?: FancytreeNode): string {
		const task = node?.data as Task;
		if (task?.name && task?.breadCrumbs) {
			return breadcrumbsWithoutName(task.breadCrumbs, task.name);
		}
		return "";
	}

	private updateTaskOnTaskDialogState() {
		const selectedAction = this.getSelectedTaskAction();
		const mergeSelected = selectedAction === "merge";
		const mergeConfirmed = $("#taskOnTaskMergeConfirmationCheckbox").is(":checked");

		$("#taskOnTaskDialog .action-option").removeClass("selected");
		$(`input[name="taskAction"]:checked`).closest(".action-option").addClass("selected");

		if (this.taskOnTaskDialog) {
			const buttonText = mergeSelected
				? messages["taskOnTask.dialog.button.merge"]
				: messages["taskOnTask.dialog.button.move"];
			this.taskOnTaskDialog.getButtons("btn-primary")
				.text(buttonText)
				.prop("disabled", mergeSelected && !mergeConfirmed);
		}
	}

	private getSelectedTaskAction(): string {
		return $(`input[name="taskAction"]:checked`).val() as string || "subtask";
	}

	protected dropTaskSave() {
		const draggedNode = this.dragDropData.otherNode;
		const targetNode = this.dragDropData.node;

		const draggedTask = draggedNode?.data as Task;
		const targetTask = targetNode?.data as Task;

		const taskAction = targetTask?.id
			? this.getSelectedTaskAction()
			: "subtask";

		return api("tasks/handleTaskOnTask.json")
			.post({
				draggedTaskId: draggedTask.id,
				targetTaskId: targetTask?.id,
				taskAction: taskAction,
				taskOnTaskDialog: ""
			})
			.done(data => {
				SecurityStatus.update(data.securityStatus);

				if (targetNode) {
					if (taskAction === "merge") {
						draggedNode.remove();
						this.reloadAndExpandNode(targetNode.key);
					} else if (taskAction === "subtask") {
						draggedNode.moveTo(targetNode);
						// Expand programmatically after DnD. BaseTree.beforeExpand only allows user-triggered expands,
						// so noEvents is required here to bypass that guard and force Fancytree to render the moved child.
						targetNode.setExpanded(true, {noEvents: true});
					}
				} else {
					draggedNode.moveTo(this.tree.getRoot());
				}
			});
	}

	private copyTask(task: Task) {
		this.clipboardTask = task;
		Notify.success(messages["task.clipboard.copy"]);
		this.updateCopyDisplay();
	}

	private clearClipboard() {
		this.clipboardTask = null;
		this.updateCopyDisplay();
	}

	private pasteTask(task: Task) {
		this.taskPasteId = task?.id;
		FormUtil.removeAllFeedback($("#taskPasteForm"));
		if (this.clipboardTask?.id) {
			const taskPasteNewName = $("#taskPasteNewName");
			this.getPasteTaskDialog().show();
			taskPasteNewName.val(`${this.clipboardTask.name} ${messages["text.duplicate"]}`);
		} else {
			BootstrapModal.error({
				title: "dialog.error.title",
				message: "task.pasteNoTask"
			});
		}
	}

	private deleteTask(task: Task | { id: number, name: string }, closeTaskDialogAfterwards = false) {
		const showExistingRecordingsError = (foundRecordings: number) => {
			BootstrapModal.warn({
				title: "dialog.error.delete.task.title",
				message: messages.withArguments("dialog.error.delete.task", foundRecordings)
			});
		};

		const showDeleteTaskDialog = () => {
			BootstrapModal.confirm({
				title: "dialog.warning.title",
				message: `<form class="form-horizontal">
									<div>${messages["dialog.confirm.delete.task"]}</div>
									<div class="form-group mt-5">
										<label for="confirmDeleteTextInput" class="col-lg-12">
											${messages.withArguments("dialog.confirm.delete.task.info", htmlEscape(task.name))}
										</label>
										<div class="col-lg-12">
											<input type="text" id="confirmDeleteTextInput" class="form-control" />
										</div>
									</div>
								</form>`,
				destroyOnClose: false,
				escapeText: false,
				yesLabel: "confirmation.delete",
				yesAction: (modal, button) => {
					const confirmDeleteTextInput = modal.find("#confirmDeleteTextInput");
					Input.removeFeedback(confirmDeleteTextInput);

					if (confirmDeleteTextInput.val() === messages["dialog.confirm.delete.task.comparison"]) {
						this.deleteTaskRequest(task.id).always(() => {
							Button.resetLoading(button);
							modal.close();
						});
					} else {
						Input.addFeedback(confirmDeleteTextInput, "has-error", messages["dialog.confirm.delete.task.error"]);
						setTimeout(() => Button.resetLoading(button), 200);
					}
				},
				noLabel: "confirmation.cancel"
			});
		};

		api("tasks/checkIfProjectTimeExistsForTask.json")
			.get({
				id: task.id
			})
			.done(data => {
				if (data.foundRecordings === 0) {
					showDeleteTaskDialog();
				} else {
					showExistingRecordingsError(data.foundRecordings);
				}
				if (closeTaskDialogAfterwards) {
					this.taskDialog.closeDialog();
				}
			});
	}

	private deleteTaskRequest(taskId: number): JQueryPromise<unknown> {
		return api("tasks/deleteTask.json")
			.post({id: taskId})
			.done(data => {
				SecurityStatus.update(data.securityStatus);

				this.tree.getNodeByKey(taskId + "")?.remove();
			});
	}

	private disableTask(task: Task) {
		api("tasks/disableTask.json")
			.post({id: task.id})
			.done(data => {
				SecurityStatus.update(data.securityStatus);

				const node = this.tree.getNodeByKey(task.id + "");
				const nodeTask = node.data as Task;
				nodeTask.disabled = true;
				node.render(true);
			});
	}

	private copyTaskLink(task: Task) {
		navigator.clipboard.writeText(EntityLinks.getTaskLink(task.taskId, task.breadCrumbs))
			.then(() => {
				Notify.success(messages["taskLink.copied"]);
				EntityLinks.trackTaskLinkCreated(task.taskId);
			}, () => {
				// Do nothing if browser cannot copy to clipboard
			})
	}

	private editTask(task: Task) {
		api("tasks/loadTaskDetails.json")
			.get({id: task.id})
			.done(e => {
				this.taskDialog.edit(e);
			});
	}

	private addSubtask(task: Task) {
		api("tasks/loadTaskDetails.json")
			.get({id: task.id})
			.done(e => {
				this.taskDialog.addSubtask(e);
			});
	}

	private savePasteTaskDialog() {
		api("tasks/pasteTask.json")
			.post({
				targetId: this.taskPasteId,
				clipboardTaskId: this.clipboardTask.id,
				name: $("#taskPasteNewName").val()
			})
			.always(() => {
				this.taskPasteDialog.resetLoadingButtons();
			})
			.done(data => {
				SecurityStatus.update(data.securityStatus);

				this.clearClipboard();
				$("#taskPasteNewName").val("");
				this.closePasteTaskDialog();
				this.reloadAndExpandNode(this.taskPasteId);

				Notify.success(messages["task.clipboard.success"]);
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors($("#taskPasteForm"), response.responseJSON.fieldErrors);
				}
			});
	}

	private closePasteTaskDialog() {
		this.taskPasteDialog.close();
	}

	private getPasteTaskDialog() {
		if (this.taskPasteDialog == null) {
			this.taskPasteDialog = this.createPasteTaskDialog();
		}
		return this.taskPasteDialog;
	}

	private createPasteTaskDialog() {
		return new BootstrapModal.Modal({
			title: "pasteTaskName.header",
			message: $(`#taskPasteForm`),
			buttons: [{
				text: "dialog.save",
				action: () => {
					this.savePasteTaskDialog();
				},
				css: "btn-primary"
			}, {
				text: "dialog.cancel",
				action: () => {
					this.closePasteTaskDialog();
				}
			}],
			onOpen: () => {
				$("#taskPasteNewName")
					.select()
					.focus();
			}
		});
	}

	private initTaskDialog() {
		this.taskDialog = new TaskAdministrationDialog({
			dialogId: "#taskAdministrationDialog",
			billingAddressCountryId: this.config.billingAddressCountryId
		});
	}

	private showAddTaskDialog() {
		if (this.checkAddTaskAllowed()) {
			this.taskDialog.create();
		}
	}

	private checkAddTaskAllowed(): boolean {
		if (!SecurityStatus.isAddTaskAllowed()) {
			BootstrapModal.info({
				title: "limit.task.title",
				message: "limit.task"
			});
			return false;
		}
		return true;
	}

	private updateCopyDisplay() {
		const panel = $("#copiedTaskInfoPanel");
		const element = $("#copiedTaskInfo");

		if (this.clipboardTask) {
			panel.show();
			element.text(this.clipboardTask.name);
		} else {
			panel.hide();
			element.text("");
		}
	}
}
